import React, { useState, useEffect, useContext } from 'react';

import { ShareContext } from '../context/sharecontext';
import * as Constants from '../Constants';
import LoadingImg from '../images/loading.gif';

import IdLayout from '../components/_layout';
import IdInput from '../components/idInput';
import IdSelect from '../components/idSelect';

const DEFAULT_SETTING = {
    isEnabled: true,
    faceMatchThreshold: 80,
    faceMatchMaxRetries: 3,
    livenessActions: 'blink,turnleft,turnright',
    livenessActionCount: 1,
    livenessMaxRetries: 3,
    retainCapturedImages: false,
};

/**
 * FRD 5.3/5.4/7's "configurable per bank, not hard-coded" verification settings
 * (CompanyVerificationSetting): thresholds, allowed liveness actions, retry limits, and image
 * retention. Only Admin/superadmin may create or change these (VerificationAdminController);
 * a bank's own Com-Owner(s) can view their bank's settings here but not edit them.
 *
 * Role note (explicit clarification): "Admin" = platform SuperAdmin. "Com-Owner" = the
 * per-company/bank admin role, and a company can have MULTIPLE Com-Owner users — any of them
 * viewing this page sees their own bank's settings via profile.companyId, not a single
 * designated owner.
 */
function VerificationSettings() {
    const { profile, token } = useContext(ShareContext);
    const isPlatformAdmin = profile?.userType === 'superadmin' || profile?.userType === 'Admin';

    const [companies, setCompanies] = useState([]);
    const [companyId, setCompanyId] = useState(isPlatformAdmin ? 0 : (profile?.companyId || 0));
    const [setting, setSetting] = useState(DEFAULT_SETTING);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        Constants.seo({ title: 'Verification Settings', metaDescription: '' });
        if (isPlatformAdmin) {
            loadCompanies();
        } else if (profile?.companyId) {
            loadSetting(profile.companyId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile]);

    const loadCompanies = async () => {
        try {
            const resp = await fetch(`${Constants.rooturl}/customer/companies`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': token },
            });
            const data = await resp.json();
            if (data.code === 0) {
                setCompanies(data.coms || []);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const loadSetting = async (cid) => {
        if (!cid) return;
        setLoading(true);
        setErr('');
        setSaved(false);
        try {
            const resp = await fetch(`${Constants.rooturl}/VerificationAdmin/settings/${cid}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': token },
            });
            const data = await resp.json();
            setLoading(false);
            if (data.code === 0) {
                setSetting(data.setting ? { ...DEFAULT_SETTING, ...data.setting } : { ...DEFAULT_SETTING, companyId: cid });
            } else {
                setErr(data.status || 'Could not load settings.');
            }
        } catch (error) {
            setLoading(false);
            console.error(error);
            setErr(Constants.serverErr);
        }
    };

    const onCompanyChange = (e) => {
        const cid = Number(e.target.value);
        setCompanyId(cid);
        loadSetting(cid);
    };

    const onSave = async (e) => {
        e.preventDefault();
        if (!companyId) {
            setErr('Select a bank first.');
            return;
        }
        setLoading(true);
        setErr('');
        setSaved(false);
        try {
            const v = { ...setting, companyId };
            const resp = await fetch(`${Constants.rooturl}/VerificationAdmin/settings`, {
                method: 'POST',
                body: JSON.stringify(v),
                headers: { 'Content-Type': 'application/json, text/plain', 'Authorization': token },
            });
            const data = await resp.json();
            setLoading(false);
            if (data.code === 0) {
                setSaved(true);
            } else {
                setErr(data.status || 'Could not save settings.');
            }
        } catch (error) {
            setLoading(false);
            console.error(error);
            setErr(Constants.serverErr);
        }
    };

    const companyOptions = companies.map((c) => ({ value: c.id, text: c.name }));

    return (
        <IdLayout active={'verificationsettings'} profile={profile}>
            <div style={{ display: 'flex', flex: 1, padding: 8, flexDirection: 'column' }}>
                <div style={{ display: 'flex', backgroundColor: 'white', flex: 1, flexDirection: 'column', padding: 16 }}>
                    <h2>Verification Settings</h2>
                    <p style={{ fontSize: 13 }}>Face Match &amp; Liveness thresholds, allowed actions and retry limits for a bank.</p>

                    {isPlatformAdmin && (
                        <IdSelect
                            onChange={onCompanyChange}
                            value={companyId}
                            disabledText="Select Bank"
                            items={companyOptions}
                            label="Bank"
                        />
                    )}

                    {err && <div style={{ fontSize: 15, color: 'red', textAlign: 'center', padding: 15, fontWeight: 'bold' }}>{err}</div>}
                    {saved && <div style={{ fontSize: 14, color: 'green', textAlign: 'center', padding: 8 }}>Saved.</div>}

                    {loading && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, flexDirection: 'column' }}>
                            <img src={LoadingImg} style={{ width: 200, marginLeft: 'auto', marginRight: 'auto' }} alt="Loading" />
                        </div>
                    )}

                    {!loading && companyId > 0 && (
                        <form onSubmit={onSave}>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 16 }}>
                                <label style={{ marginRight: 8 }}>
                                    <input
                                        type="checkbox"
                                        checked={!!setting.isEnabled}
                                        disabled={!isPlatformAdmin}
                                        onChange={(e) => setSetting({ ...setting, isEnabled: e.target.checked })}
                                    /> Feature Enabled
                                </label>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row' }}>
                                <IdInput type='number' label='Face Match Threshold (0-100)' value={setting.faceMatchThreshold} disabled={!isPlatformAdmin} onChange={(e) => setSetting({ ...setting, faceMatchThreshold: e.target.value })} required />
                                <IdInput type='number' label='Face Match Max Retries' value={setting.faceMatchMaxRetries} disabled={!isPlatformAdmin} onChange={(e) => setSetting({ ...setting, faceMatchMaxRetries: e.target.value })} required />
                            </div>
                            <div style={{ fontSize: 12, color: '#777', margin: '8px 0' }}>
                                Every liveness check now always requires all 3 actions (blink, turn left, turn right),
                                presented in a random order each attempt — this is fixed platform-wide and no longer
                                configurable per bank, so there's no single predictable sequence someone could defeat
                                with a pre-recorded video.
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row' }}>
                                <IdInput type='number' label='Liveness Max Retries' value={setting.livenessMaxRetries} disabled={!isPlatformAdmin} onChange={(e) => setSetting({ ...setting, livenessMaxRetries: e.target.value })} required />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                                <label style={{ marginRight: 8 }}>
                                    <input
                                        type="checkbox"
                                        checked={!!setting.retainCapturedImages}
                                        disabled={!isPlatformAdmin}
                                        onChange={(e) => setSetting({ ...setting, retainCapturedImages: e.target.checked })}
                                    /> Retain Captured Images (NDPR — leave off unless documented)
                                </label>
                            </div>

                            {isPlatformAdmin && (
                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16, marginBottom: 16 }}>
                                    <button className='greenbutton' type="submit">Save Settings</button>
                                </div>
                            )}
                            {!isPlatformAdmin && (
                                <p style={{ fontSize: 12, color: '#777', marginTop: 16 }}>
                                    Only IdVerify platform admins can change these settings. Contact support to request a change.
                                </p>
                            )}
                        </form>
                    )}
                </div>
            </div>
        </IdLayout>
    );
}

export default VerificationSettings;
